import EditOutlined from "@mui/icons-material/EditOutlined";
import KeyboardReturnOutlined from "@mui/icons-material/KeyboardReturnOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import SearchOffOutlined from "@mui/icons-material/SearchOffOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { ModuleRegistry } from "ag-grid-community";
import { RichSelectModule } from "ag-grid-enterprise";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { loanDrilldownSet } from "../../redux/actions/loanDrilldownActions";

import Flx from "../../components/layout/Flx";
import Htag from "../../components/typography/Htag";
import SlowRequestPrompt from "../../components/SlowRequestPrompt/SlowRequestPrompt";

import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import isObject from "lodash/isObject";
import isString from "lodash/isString";
import size from "lodash/size";
import RffLoanDataGroup from "../../components/finalForm/shared/RffLoanDataGroup";

import {
  AdminPanelSettingsOutlined,
  ApartmentOutlined,
  AutoAwesomeOutlined,
  BusinessOutlined,
  CalculateOutlined,
  ChecklistOutlined,
  ConstructionOutlined,
  ContactsOutlined,
  DescriptionOutlined,
  ErrorOutline,
  EventAvailableOutlined,
  FolderOutlined,
  VerifiedOutlined,
  ForumOutlined,
  GavelOutlined,
  HandshakeOutlined,
  HomeWorkOutlined,
  InfoOutlined,
  LocalOfferOutlined,
  LocalShippingOutlined,
  LockOutlined,
  PaidOutlined,
  PeopleOutlined,
  PersonSearchOutlined,
  PushPin,
  PushPinOutlined,
  ReceiptLongOutlined,
  ReportProblemOutlined,
  RuleOutlined,
  SavingsOutlined,
  StarOutlineRounded,
  StarRounded,
  TrendingUpOutlined,
  WorkspacePremiumOutlined,
} from "@mui/icons-material";
import { red } from "@mui/material/colors";
import { formatBorrowerObjectFullName } from "../../_src_shared/utils/functions/formatBorrowerObjectFullName";
import LoanChecklist from "../../components/common/Loan/LoanChecklist/LoanChecklist";
import LoanPermittedUsersAvatarStack from "../../components/common/Loan/LoanPermittedUsersAvatarStack";
import LoanTagsModal from "../../components/common/Loan/LoanTags/LoanTagsModal";
import UpdateLoanDataModal from "../../components/common/Loan/UpdateLoanDataModal";
import RffDisplayField from "../../components/finalForm/inputs/RffDisplayField";
import RffInputWrapper from "../../components/finalForm/shared/RffInputWrapper";
import { useDrilldownFieldFocus } from "./shared/useDrilldownFieldFocus";
import ScreenContent from "../../components/layout/ScreenContent";
import BorrowerPortalSearchModal from "../../components/modals/common/BorrowerPortalSearchModal";
import GlobalLoanFilePreviewSidebar from "./tabs/LoanDrilldownDocumentsTab/GlobalLoanFilePreviewSidebar";
import LoanDocumentUploadModal from "./shared/LoanDocumentUploadModal";
import LoanBorrowerSidebar from "../../components/sidebar/LoanBorrowerSidebar/LoanBorrowerSidebar";
import LoanRelatedContactSidebar from "../../components/sidebar/LoanRelatedContactSidebar/LoanRelatedContactSidebar";
import LoanSubjectPropertySidebar from "../../components/sidebar/LoanSubjectPropertySidebar/LoanSubjectPropertySidebar";
import Txt from "../../components/typography/Txt";
import { formatDate } from "../../_src_shared/utils/functions/formatDate";
import UpdateLoanRootDataFieldsModal from "../../components/common/Loan/UpdateLoanRootDataFieldsModal";
import { loanDrilldownSidebarTypes } from "../../constants/enums/loanDrilldownScreenSidebarOptions";
import { useEnv } from "../../hooks/useEnv";
import {
  usePipeline,
  useIsAcquisitions,
} from "../../pipelines/PipelineContext";
import {
  useEnsureUsers,
  useEnsureCorrespondents,
} from "../../hooks/useEnsureReferenceData";
import { useCorrespondentOptions } from "../../hooks/useCorrespondentOptions";
import { useMediaQueryHook } from "../../hooks/useMediaQueryHook";
import { usePermissionsHook } from "../../hooks/usePermissionsHook";
import { useReduxLoanDrilldownHook } from "../../hooks/useReduxLoanDrilldownHook";
import { useUnderwritingHook } from "../../hooks/useUnderwritingHook";
import { useUserPreferences } from "../../hooks/utils/useUserPreferences";
import { useWorkspaceTabs } from "../../hooks/utils/useWorkspaceTabs";
import { useDealPresence } from "../../realtime/useDealPresence";
import { loanPipelineClear } from "../../redux/actions/loanPipelineActions";
import { navSidebarClose } from "../../redux/actions/navSidebarActions";
import { DETAIL_SIDEBAR_CONTENT_TYPES } from "../../redux/reducers/detailSidebarReducer";
import { nsKey } from "../../utils/storage/partnerStorage";
import LoanDrilldownRouterTabSwitcher from "./components/LoanDrilldownRouterTabSwitcher";
import { StatusChanger } from "./tabs/LoanDrilldownSummaryTab/summaryShared";
import LoanCorrespondentSidebar from "./components/LoanCorrespondentSidebar";
import LoanHeaderGuarantors from "./components/LoanHeaderGuarantors";
import LoanHeaderProperties from "./components/LoanHeaderProperties";
import { Chip as HeaderValueChip, T as HeaderChipT } from "./components/LoanHeaderChipList";
import SummaryTabViewRouter from "./components/SummaryTabViewRouter";
import LoanDrilldownChatTab from "./tabs/LoanDrilldownChatTab/LoanDrilldownChatTab";
import LoanChatFloatingButton from "./tabs/LoanDrilldownChatTab/LoanChatFloatingButton";
import LoanDrilldownLiveBar from "./components/LoanDrilldownLiveBar";
import { canSeeLoanChat } from "./tabs/LoanDrilldownChatTab/loanChatAccess";
import { useAuth } from "../../auth/AuthProvider";
import SummaryViewSelector from "./components/SummaryViewSelector";
import { useLoanNavIcons } from "./components/useLoanNavIcons";
import LoanDrilldownScreenSidebar from "./LoanDrilldownScreenSidebar";
import LoanDrilldownApplicantsTab from "./tabs/LoanDrilldownApplicantsTab/LoanDrilldownApplicantsTab";
import LoanDrilldownExperienceTab from "./tabs/LoanDrilldownExperienceTab";
import LoanDrilldownAppraisalsTab from "./tabs/LoanDrilldownAppraisalsTab/LoanDrilldownAppraisalsTab";
import LoanDrilldownChecklistTab from "./tabs/LoanDrilldownChecklistTab/LoanDrilldownChecklistTab";
import LoanDrilldownClosingTab from "./tabs/LoanDrilldownClosingTab";
import LoanDrilldownCommunicationTab from "./tabs/LoanDrilldownCommunicationTab/LoanDrilldownCommunicationTab";
import LoanDrilldownConditionsAndExceptionsTab from "./tabs/LoanDrilldownConditionsAndExceptionsTab";
import LoanDrilldownConditionsChecklistTab from "./tabs/LoanDrilldownConditionsChecklistTab";
import LoanDrilldownConditionsTab from "./tabs/LoanDrilldownConditionsTab";
import LoanDrilldownDetailsTab from "./tabs/LoanDrilldownDetailsTab/LoanDrilldownDetailsTab";
import LoanDrilldownLoanInfoRedesignTab from "./tabs/LoanDrilldownLoanInfoRedesignTab";
import LoanDrilldownDocumentsTab from "./tabs/LoanDrilldownDocumentsTab/LoanDrilldownDocumentsTab";
import AcquisitionsClosedPackageTab from "./tabs/AcquisitionsClosedPackageTab/AcquisitionsClosedPackageTab";
import AcquisitionsEconomicsTab from "./tabs/AcquisitionsEconomicsTab/AcquisitionsEconomicsTab";
import BudgetDrawsTab from "./tabs/BudgetDrawsTab/BudgetDrawsTab";
import AcquisitionsTradeBanner from "./components/AcquisitionsTradeBanner";
import LoanSettingsMenu from "./components/LoanSettingsMenu";
import AiReviewTab from "./tabs/AiReviewTab/AiReviewTab";
import LoanDrilldownAdminTab from "./tabs/LoanDrilldownAdminTab/LoanDrilldownAdminTab";
import LoanDrilldownCardGalleryTab from "./tabs/LoanDrilldownCardGalleryTab/LoanDrilldownCardGalleryTab";
import LoanDrilldownEntityTab from "./tabs/LoanDrilldownEntityTab";
import LoanDrilldownEntityOriginalTab from "./tabs/LoanDrilldownEntityOriginalTab/LoanDrilldownEntityOriginalTab";
import LoanDrilldownExceptionsRedesignTab from "./tabs/LoanDrilldownExceptionsRedesignTab/LoanDrilldownExceptionsRedesignTab";
import LoanDrilldownExceptionsTab from "./tabs/LoanDrilldownExceptionsTab/LoanDrilldownExceptionsTab";
import LoanDrilldownFeesRedesignTab from "./tabs/LoanDrilldownFeesRedesignTab";
import LoanDrilldownFeesTab from "./tabs/LoanDrilldownFeesTab/LoanDrilldownFeesTab";
import LoanDrilldownFilesTab from "./tabs/LoanDrilldownFilesTab/LoanDrilldownFilesTab";
import LoanDrilldownKeyDatesAndDocumentsTab from "./tabs/LoanDrilldownKeyDatesAndDocumentsTab";
import LoanDrilldownLetterOfIntentTab from "./tabs/LoanDrilldownLetterOfIntentTab";
import LoanDrilldownLiquidityCalculationsTab from "./tabs/LoanDrilldownLiquidityCalculationsTab";
import LoanDrilldownDscrCalculationsTab from "./tabs/LoanDrilldownDscrCalculationsTab";
import LoanDrilldownDscrWorkbookRedesignTab from "./tabs/LoanDrilldownDscrWorkbookRedesignTab";
// V2 is the live, restyled Loan Lock tab; the original is kept as a fallback,
// reachable via the "View legacy" button at /loan-lock-legacy.
import LoanDrilldownLoanLockTab from "./tabs/LoanDrilldownLoanLockTab/LoanDrilldownLoanLockTabV2";
import LoanDrilldownLoanLockTabLegacy from "./tabs/LoanDrilldownLoanLockTab/LoanDrilldownLoanLockTab";
import LoanDrilldownLoanRelatedContactsTab from "./tabs/LoanDrilldownLoanRelatedContactsTab/LoanDrilldownLoanRelatedContactsTab";
import { isConstructionProduct } from "./tabs/LoanDrilldownLoanRelatedContactsTab/contactsSections";
import LoanDrilldownLoanTermsTab from "./tabs/LoanDrilldownLoanTermsTab";
import LoanDrilldownOrdersTab from "./tabs/LoanDrilldownOrdersTab/LoanDrilldownOrdersTab";
import LoanDrilldownPropertiesTab from "./tabs/LoanDrilldownPropertiesTab/LoanDrilldownPropertiesTab";
import LoanDrilldownSizerTab from "./tabs/LoanDrilldownSizerTab";
import LoanDrilldownSubjectPropertiesTab from "./tabs/LoanDrilldownSubjectPropertiesTab/LoanDrilldownSubjectPropertiesTab";
import LoanDrilldownTermsSheetHhbTab from "./tabs/LoanDrilldownTermsSheetHhbTab";
import LoanDrilldownWireAndFundingTab from "./tabs/LoanDrilldownWireAndFundingTab";
import colors, { amber } from "../../theme/colors";

// Register AG Grid modules
ModuleRegistry.registerModules([RichSelectModule]);
const initialLoanData = {
  _id: "67f44f69e685b472c479f7dd",
};

/** =====================
 * MAIN SCREEN COMPONENT
 * ===================== */
const LoanDrilldownScreen = ({ embedded = false } = {}) => {
  // The drilldown resolves user emails (assignees, comment authors, condition
  // owners) and the loan's correspondent from the shared Redux slices — pull
  // both lazily (cache-gated) rather than via a global eager fetch.
  useEnsureUsers();
  useEnsureCorrespondents();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // The URL now carries the loan `_id` directly (route is `/loan/:id`, where
  // `:id` is the Mongo `_id`). No loanNumber→_id resolution / pipeline lookup.
  const { id: loanId } = useParams();
  const { getLoanById } = useUnderwritingHook();
  const { clearLoanDrilldownData } = useReduxLoanDrilldownHook();

  // Join this loan's `deal:{id}` Web PubSub viewer group while the drilldown is
  // open so the backend can target "current viewers" notifications (no-ops until
  // realtime is configured). The route param is the loan _id (= dealId).
  useDealPresence(loanId);

  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { displayName } = useEnv();

  // While viewing a loan, retitle the browser tab "EV | Loan #{loanNumber}" so
  // multiple open loans are distinguishable. Restore the app default on unmount
  // (the global title effect in index.js only re-runs on displayName change).
  const loanNumber = loanDrilldown?.loanNumber;
  useEffect(() => {
    if (isNil(loanNumber)) return;
    document.title = `EV | Loan #${loanNumber}`;
    return () => {
      document.title = `${displayName} | Portal`;
    };
  }, [loanNumber, displayName]);

  // Only surface the "Try Again" affordance once the spinner has been up for a
  // while — a quick load shouldn't flash a retry button.
  const [showRetry, setShowRetry] = useState(false);
  useEffect(() => {
    if (!isNil(loanDrilldown)) {
      setShowRetry(false);
      return;
    }
    const timer = setTimeout(() => setShowRetry(true), 5000);
    return () => clearTimeout(timer);
  }, [loanDrilldown]);

  // Guards against duplicate / runaway fetches: which loan id a request is
  // currently in flight for (null = none).
  const fetchInFlightForRef = useRef(null);

  const fetchLoanById = useCallback(
    (id) => {
      fetchInFlightForRef.current = id;
      getLoanById({
        loanId: id,
        // Cold load for this screen — there is no loan in state yet, so there
        // is nothing a concurrent write could be reverting. Opt out of the
        // stale-read guard so a write racing the very first fetch can't leave
        // the drilldown empty. (Refresh paths keep the guard.)
        allowStale: true,
        onSuccessFn: (data) => dispatch(loanDrilldownSet(data)),
        onFailFn: () => dispatch(loanDrilldownSet({})),
        onCompleteFn: () => {
          if (fetchInFlightForRef.current === id)
            fetchInFlightForRef.current = null;
        },
      });
    },
    [getLoanById, dispatch],
  );

  // Reset the loan-drilldown Redux state (loan data, fetched data, sidebars)
  // whenever we leave the drilldown route entirely — e.g. back to the pipeline
  // or anywhere else. Navigating between loans (param change) does NOT unmount
  // this screen, so this only fires on a true exit, not on loan-to-loan switches.
  useEffect(() => {
    return () => {
      clearLoanDrilldownData();
    };
  }, [clearLoanDrilldownData]);

  // Close the nav sidebar when entering a loan / switching loans.
  useEffect(() => {
    dispatch(navSidebarClose());
  }, [loanId, dispatch]);

  // Load (and self-heal) the loan for the current route. Depends on BOTH the
  // route id AND `loanDrilldown`, so it recovers when the data is reset to
  // `null` without a remount or id change — e.g. dev hot-reloads that reset the
  // Redux store while sitting on the drilldown (the old id-only effect never
  // re-fired, so the spinner hung forever). Guards below keep it from looping.
  useEffect(() => {
    if (loanId === "dev") {
      if (isNil(loanDrilldown)) dispatch(loanDrilldownSet(initialLoanData));
      return;
    }
    if (!isString(loanId)) {
      dispatch(loanDrilldownSet({}));
      return;
    }
    // Already have this loan loaded — don't refetch on in-loan tab navigation.
    if (loanDrilldown?._id === loanId) return;

    // Holding a DIFFERENT loan (param switch without unmount, e.g. the Lock
    // Queue master-detail rail) — clear so the spinner shows immediately; the
    // clear flips state to null and re-runs this effect, which then fetches.
    if (!isNil(loanDrilldown) && loanDrilldown._id) {
      clearLoanDrilldownData();
      return;
    }

    // From here `loanDrilldown` is null ({} means "fetched, not found" — never
    // refetch that, or a genuinely missing loan would loop). Fetch unless a
    // request for this id is already running.
    if (!isNil(loanDrilldown)) return;
    if (fetchInFlightForRef.current === loanId) return;
    fetchLoanById(loanId);
  }, [loanId, loanDrilldown, dispatch, clearLoanDrilldownData, fetchLoanById]);

  if (isNil(loanDrilldown)) {
    return (
      <Flx gap={2} column center sx={{ p: 4, height: "100vh" }}>
        <CircularProgress />
        {showRetry && (
          <Button
            variant="text"
            size="small"
            startIcon={<RefreshOutlined />}
            onClick={() => fetchLoanById(loanId)}
          >
            Try Again
          </Button>
        )}
        <SlowRequestPrompt
          context="this loan"
          relatedDealId={loanId}
          details={{ "Loan ID": loanId, Request: "GET /loan/:id" }}
        />
      </Flx>
    );
  }

  if (isObject(loanDrilldown) && isEmpty(loanDrilldown)) {
    return (
      <Flx gap={1} column center sx={{ p: 4, height: "100vh" }}>
        <SearchOffOutlined fontSize="large" />
        <Htag h1>No Loan Found</Htag>
        <Txt>We encountered a problem trying to retrieve your loan.</Txt>
        <Flx sx={{ mt: 1 }}>
          <Button
            startIcon={<KeyboardReturnOutlined />}
            onClick={() => navigate("/loans")}
          >
            Return to Pipeline
          </Button>
        </Flx>
      </Flx>
    );
  }

  // We have a non-empty loan object, but it's not (yet) the loan for this route
  // — stale data from the previous screen or a partial stub while the fetch for
  // `:id` is still resolving. Keep the spinner up instead of flashing the wrong
  // loan / "Loan #undefined" header. (The "dev" sandbox route uses a fixture
  // whose _id won't match, so it's exempt.) The fetch effect above is already
  // loading the correct loan.
  if (loanId !== "dev" && isString(loanId) && loanDrilldown?._id !== loanId) {
    return (
      <Flx gap={2} column center sx={{ p: 4, height: "100vh" }}>
        <CircularProgress />
        <SlowRequestPrompt
          context="this loan"
          relatedDealId={loanId}
          details={{ "Loan ID": loanId, Request: "GET /loan/:id" }}
        />
      </Flx>
    );
  }

  return <LoanDrilldownContent navigate={navigate} embedded={embedded} />;
};

/** =========================
 * CONTENT RENDERER
 * ========================= */
const LoanDrilldownContent = memo(({ navigate, embedded }) => {
  const detailSidebar = useSelector((state) => state.detailSidebar);
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const showChecklist = useSelector((state) => state.sidebar?.checklist);
  // Derive the drilldown base from the URL rather than hardcoding `/loan/:id`,
  // so the screen can be embedded under a different base (e.g. the Lock Queue's
  // master-detail at `/lock-queue/:id`) and its tab links / catch-all redirect
  // stay within that base. Path is always `<base>/<id>/<tab...>`; the base is
  // everything up to and including the `/<id>` segment.
  const location = useLocation();
  const baseUrl = useMemo(() => {
    const id = loanDrilldown?._id;
    if (!id) return "";
    const marker = `/${id}`;
    const idx = location.pathname.indexOf(marker);
    return idx === -1
      ? `/loan/${id}`
      : location.pathname.slice(0, idx + marker.length);
  }, [loanDrilldown, location.pathname]);
  const { pinned } = showChecklist;
  const dispatch = useDispatch();
  // `?focus=<field>` deep links (Closing readiness → "Go to …").
  useDrilldownFieldFocus();

  // NOTE: the loan's internal-appraisal orders are NOT prefetched here. The
  // backend route (GET /loan-processing/{dealId}/appraisal-orders) isn't
  // deployed yet (404), so loading it on every loan load is wasted. The Orders
  // tab fetches it lazily when it's actually opened — restore a prefetch here
  // for the NavigationBar badge once the endpoint is live.

  const selectedBorrower = useMemo(() => {
    if (
      detailSidebar.isOpen &&
      detailSidebar.contentType === DETAIL_SIDEBAR_CONTENT_TYPES.BORROWER
    ) {
      const filteredBorrower = loanDrilldown?.borrowers?.find(
        (b) => b._id === detailSidebar.data?._id,
      );
      return filteredBorrower || detailSidebar.data;
    }
    return null;
  }, [detailSidebar, loanDrilldown?.borrowers]);

  const selectedSubjectProperty = useMemo(() => {
    if (
      detailSidebar.isOpen &&
      detailSidebar.contentType ===
        DETAIL_SIDEBAR_CONTENT_TYPES.SUBJECT_PROPERTY
    ) {
      const filtered = loanDrilldown?.subjectProperties?.find(
        (p) => p._id === detailSidebar.data?._id,
      );
      return filtered || detailSidebar.data;
    }
    return null;
  }, [detailSidebar, loanDrilldown?.subjectProperties]);

  const selectedLoanRelatedContact = useMemo(() => {
    if (
      detailSidebar.isOpen &&
      detailSidebar.contentType ===
        DETAIL_SIDEBAR_CONTENT_TYPES.LOAN_RELATED_CONTACT
    ) {
      const filtered = loanDrilldown?.loanRelatedContacts?.find(
        (c) => c._id === detailSidebar.data?._id,
      );
      return filtered || detailSidebar.data;
    }
    return null;
  }, [detailSidebar, loanDrilldown?.loanRelatedContacts]);
  return (
    <Flx
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        // background: "red",
        // background: "#f8fafc",
        // background: "#f8fafc",
        // background: "rgb(250, 250, 248)",
        // background: "#f8f7f5",
        // ----
        background: "#f1f5f9", // bv
        // background: "#f5f6fa",
        overflow: "hidden",

        // background: "#f5f8fa",
      }}
    >
      {/* <LoanDrilldownTotalsLeftSidebar
        show={showTotals}
        onClose={closeTotals}
        onOpen={toggleShowTotals}
      /> */}
      <Box
        id="overflow-root"
        sx={{
          overflowY: "auto",
          flexGrow: 1,

          height: "100%",
          display: "flex",
          flexDirection: "column",
          // flexGrow: 1,
          // "&::-webkit-scrollbar-track": { backgroundColor: "#f8fafc" },
          "&::-webkit-scrollbar-track": { backgroundColor: "#f5f8fa" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.2)",
          },
          scrollbarColor: "#bbb #f5f8fa",
        }}
      >
        {/* In embedded mode (Lock Queue master-detail) the whole drilldown
            header — title row + overview quick-items — is hidden, leaving just
            the tab switcher and the content below it. The lock context lives in
            the dedicated middle pane. */}
        {!embedded && <DrilldownHeader />}
        {!embedded && (
          <ScreenContent
            padding={2}
            sx={{
              flexShrink: 0,
              overflowX: "auto",
              background: "#ffffff",
            }}
          >
            <OverviewDisplayFields />
          </ScreenContent>
        )}
        <NavigationBar baseUrl={baseUrl} />
        {!embedded && <LoanDrilldownLiveBar />}
        {/* Renders only for acquisition loans flagged bulkTrade. */}
        <AcquisitionsTradeBanner />
        <ContentRouter baseUrl={baseUrl} />
      </Box>
      {pinned && <LoanChecklist />}
      {/* <LoanDrilldownScreenSidebar /> */}
      <LoanDrilldownScreenSidebar />
      <GlobalLoanFilePreviewSidebar />
      <LoanBorrowerSidebar loan={loanDrilldown} borrower={selectedBorrower} />
      {selectedLoanRelatedContact && (
        <LoanRelatedContactSidebar contact={selectedLoanRelatedContact} />
      )}
      {selectedSubjectProperty && (
        <LoanSubjectPropertySidebar property={selectedSubjectProperty} />
      )}
      {/* Sizer upload now renders through the drilldown sidebar system
          (DrilldownSidebarSizerUpload, keyed on SIZER_UPLOAD) as a pop-out pane.
          The old LoanSizerUploader modal mount was removed so it doesn't double
          up with the sidebar. */}
      {/* Floating chat launcher — self-gates (loan loaded + access), hidden on
          the Chat tab. Not shown in the embedded (lock-context) view. */}
      {!embedded && <LoanChatFloatingButton />}
      {/* Global reusable document uploader — opened from anywhere in the
          drilldown via docUploadModalOpen(assignTo). */}
      <LoanDocumentUploadModal />
    </Flx>
  );
});

/** =========================
 * DRILLDOWN HEADER
 * ========================= */
// Small pill that makes the loan's pipeline obvious the moment you drill in:
// Pre-Close (underwriting) vs Acquisition (post-close). Cool teal vs warm amber
// so the two are distinguishable at a glance.
const PipelineTypeBadge = memo(() => {
  const isAcquisition = useIsAcquisitions();
  const cfg = isAcquisition
    ? {
        label: "Acquisition",
        tip: "Loan acquisition (post-close) pipeline",
        fg: "#854f0b",
        bg: "#faeeda",
        border: "#f5c97a",
        dot: "#d97706",
      }
    : {
        label: "Pre-Close",
        tip: "Pre-close underwriting pipeline",
        fg: "#0e7490",
        bg: "#e7f4f8",
        border: "#b6e3ef",
        dot: "#0891b2",
      };
  return (
    <Tooltip title={cfg.tip} arrow>
      <Flx
        ac
        g={0.7}
        sx={{
          flexShrink: 0,
          px: 1,
          py: 0.4,
          borderRadius: 999,
          background: cfg.bg,
          border: `1px solid ${cfg.border}`,
          color: cfg.fg,
          fontSize: 11.5,
          fontWeight: 700,
          letterSpacing: "0.02em",
          lineHeight: 1,
          whiteSpace: "nowrap",
        }}
      >
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: cfg.dot,
            flexShrink: 0,
          }}
        />
        {cfg.label}
      </Flx>
    </Tooltip>
  );
});

const DrilldownHeader = memo(({ hideActions }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);

  const loanTitle = useMemo(() => {
    if (isNil(loanDrilldown?.loanName)) {
      return `Loan #${loanDrilldown?.loanNumber}`;
    }
    return loanDrilldown?.loanName || `Loan #${loanDrilldown?.loanNumber}`;
  }, [loanDrilldown]);

  // Bespoke header (formerly the shared <ScreenHeader />). Reproduces its
  // white app-bar chrome (pt/px:2, pb:0, relative), the space-between inner
  // row, and the title + edit-button cluster — kept self-contained so the
  // drilldown header can evolve independently of the shared component.
  return (
    <Box
      component="header"
      sx={{
        backgroundColor: "#fff",
        position: "relative",
        flexShrink: 0,
        pt: 3,
        px: 2,
        pb: 0,
      }}
    >
      <Box
        className="header-inner-wrapper"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Flx column g={1} sx={{ flexGrow: 1 }}>
          <Flx ac wrap gap={1}>
            <Htag
              h1
              weight="bold"
              size={300}
              sx={{
                // color: (theme) => theme.palette.primary.main,
                fontSize: "20px",
              }}
            >
              {loanTitle}
            </Htag>
            <PipelineTypeBadge />
            {!hideActions && <EditLoanButtonWithModals />}
          </Flx>
        </Flx>
        {!hideActions && (
          <Flx ac gap={1} sx={{ flexShrink: 0 }}>
            <LoanPermittedUsersAvatarStack label={null} />
            <SummaryViewSelector />
            <LoanSettingsMenu />
          </Flx>
        )}
      </Box>
    </Box>
  );
});

const SearchBorrowerPortalUsersButton = ({ loan }) => {
  const [show, setShow] = useState(false);

  return (
    <>
      <Button
        // sx={{ mr: -0.6, ml: -0.6 }}
        size="small"
        startIcon={
          <PersonSearchOutlined sx={{ fontSize: "13px" }} className="thin" />
        }
        onClick={() => setShow(true)}
      >
        Borrower Search
      </Button>
      <BorrowerPortalSearchModal show={show} onClose={() => setShow(false)} />
    </>
  );
};

/** =========================
 * NAVIGATION TABS
 * ========================= */
// Tabs that only exist in ONE pipeline, mapped to the capability that must be
// on for them to show (see src/pipelines/pipelineConfig.js). Underwriting
// (preclose) has Terms / Fees / Loan Lock; Acquisitions (postclose) has the
// Closed Package review. A tab with no entry here shows in both pipelines.
const TAB_CAPABILITY_REQUIRED = {
  terms: "loanTerms",
  fees: "fees",
  "loan-lock": "loanLock",
  checklist: "precloseChecklist",
  "closed-package": "closedPackage",
  economics: "tradeEconomics",
};

// Construction products that get the Budget & Draws tab (both pipelines).
const BUDGET_ELIGIBLE_PRODUCTS = new Set([
  "Fix and Flip",
  "Ground Up Construction",
]);

const NavigationBar = memo(({ baseUrl }) => {
  const { isUnderwriter, isAdmin, canViewLoanLock } = usePermissionsHook();
  // Density preference from the header Settings menu — off fits more tabs.
  const [showNavIcons] = useLoanNavIcons();
  const { isResfin, isLendmarq, isResilender } = useEnv();
  const { capabilities } = usePipeline();
  const isAcquisitions = useIsAcquisitions();
  const { user } = useAuth();
  const showChat = canSeeLoanChat(user?.email);
  const loanDrilldown = useSelector((state) => state.loanDrilldown);

  // Publish the pinned nav's height on the scroller as `--drilldown-nav-h`, so
  // stickies inside a tab (the Documents toolbar, the Loan Info rail, …) can
  // offset by it instead of sliding underneath. Measured rather than hardcoded
  // because the bar's height is font/zoom dependent. Consumers fall back to
  // `0px`, which is the pre-sticky behavior.
  const navRef = useRef(null);
  useEffect(() => {
    const el = navRef.current;
    const scroller = el?.closest("#overflow-root");
    if (!el || !scroller) return undefined;
    const apply = () =>
      scroller.style.setProperty("--drilldown-nav-h", `${el.offsetHeight}px`);
    apply();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Count of active (non-cancelled) internal-appraisal review orders — surfaced
  // as a badge on the Orders tab so the drilldown shows at a glance that a
  // review has been ordered.
  // Outside-party (vendor / AMC) secondary valuation orders count too; the
  // internal kind is already in `appraisalOrders`.
  const appraisalOrderCount =
    (loanDrilldown?.appraisalOrders || []).filter(
      (o) => o?.status !== "cancelled",
    ).length +
    (loanDrilldown?.secondaryValuationOrders || []).filter(
      (o) => o?.kind !== "internal" && o?.status !== "cancelled",
    ).length;
  const tabs = useMemo(() => {
    const baseTabs = [
      {
        // Summary Redesign 3 is now the official default summary tab, served
        // at the canonical /summary path.
        label: "Summary",
        path: "summary",
        icon: <DescriptionOutlined className="thin" />,
      },
      // Hidden — older summary iterations + the original "Summary" tab,
      // superseded by the new Summary (Redesign 3). Routes stay mounted in
      // ContentRouter so existing deep links still resolve.
      // {
      //   label: "Summary (Legacy)",
      //   path: "summary",
      //   icon: <DescriptionOutlined className="thin" />,
      // },
      // {
      //   label: "Summary Redesign",
      //   path: "summary-redesign-alt",
      //   icon: <DescriptionOutlined className="thin" />,
      // },
      // {
      //   label: "Summary Redesign 2",
      //   path: "summary-redesign-alt-2",
      //   icon: <DescriptionOutlined className="thin" />,
      // },
      {
        label: "Loan Info",
        path: "loan-information",
        icon: <InfoOutlined className="thin" />,
      },
      {
        label: "Documents",
        path: "documents",
        icon: <FolderOutlined className="thin" />,
      },
      {
        // Acquisitions-only (gated by the closedPackage capability). Postclose
        // diligence centers on the note + closed package, so this sits high.
        label: "Closed Package",
        path: "closed-package",
        icon: <VerifiedOutlined className="thin" />,
      },
      {
        // Acquisitions-only (tradeEconomics). Buy-rate + strip for RTL/GUC,
        // bond points for DSCR — the convention is derived server-side.
        label: "Trade Economics",
        path: "economics",
        icon: <TrendingUpOutlined className="thin" />,
      },
      {
        label: "Entity",
        path: "entity",
        icon: <BusinessOutlined className="thin" />,
      },
      {
        label: "Applicants",
        path: "applicants",
        icon: <PeopleOutlined className="thin" />,
      },
      {
        label: "Properties",
        path: "properties",
        icon: <HomeWorkOutlined className="thin" />,
      },
      {
        label: "Contacts",
        path: "contacts",
        icon: <ContactsOutlined className="thin" />,
      },
      {
        label:
          appraisalOrderCount > 0 ? (
            <>
              Orders
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 15,
                  height: 15,
                  px: "4px",
                  ml: 0.6,
                  borderRadius: "999px",
                  background: colors.action,
                  color: "#fff",
                  fontSize: "9px",
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                {appraisalOrderCount}
              </Box>
            </>
          ) : (
            "Orders"
          ),
        path: "orders",
        icon: <LocalShippingOutlined className="thin" />,
      },
      {
        label: "Terms",
        path: "terms",
        icon: <PaidOutlined className="thin" />,
      },
      {
        label: "Fees",
        path: "fees",
        icon: <ReceiptLongOutlined className="thin" />,
      },
      // Budget & Draws — construction loans only (Fix and Flip / Ground Up), on
      // BOTH pipelines. The tab content also trusts `budgetTabEligible` from its
      // GETs; this is the cheap client pre-filter (see budget-scope-of-work-draws.md).
      ...(BUDGET_ELIGIBLE_PRODUCTS.has(loanDrilldown?.loanProductType)
        ? [
            {
              label: "Budget & Draws",
              path: "budget-draws",
              icon: <ConstructionOutlined className="thin" />,
            },
          ]
        : []),
      // Loan Lock: underwriting, admin, capital markets, sales / LOs, processing,
      // closers (canViewLoanLock in usePermissionsHook). For acquisition
      // loans it additionally only shows for DSCR products; pre-close shows it
      // for every product as before.
      ...(canViewLoanLock &&
      (!isAcquisitions || loanDrilldown?.loanProductType === "DSCR")
        ? [
            {
              label: "Loan Lock",
              path: "loan-lock",
              icon: <LockOutlined className="thin" />,
            },
          ]
        : []),
      {
        label: "Liquidity Calc.",
        path: "liquidity-calculations",
        icon: <SavingsOutlined className="thin" />,
      },
      // Experience Calc. — the Experience Workbook (experience-workbook.md).
      // Shown on every loan, product type and user, on BOTH pipelines.
      {
        label: "Experience Calc.",
        path: "experience",
        icon: <WorkspacePremiumOutlined className="thin" />,
      },
      // DSCR Calc. — the evidenced income/expense rows behind the loan's DSCR.
      // DSCR product only, the same cheap client pre-filter Budget & Draws uses
      // for construction products.
      ...(loanDrilldown?.loanProductType === "DSCR"
        ? [
            {
              label: "DSCR Calc.",
              path: "dscr-calculations",
              icon: <CalculateOutlined className="thin" />,
            },
          ]
        : []),
      // Loan Chat — live team thread for this loan. Gated by loanChatAccess
      // (dev mode or an email allowlist) while it's being trialed.
      ...(showChat
        ? [
            {
              label: "Chat",
              path: "chat",
              icon: <ForumOutlined className="thin" />,
            },
          ]
        : []),
    ];

    // TEMP: Email tab hidden
    // if ((isUnderwriter || isResfin || isLendmarq) && !isResilender) {
    //   baseTabs.push({
    //     label: "Email",
    //     path: "communication",
    //     icon: <MailOutlined className="thin" />,
    //   });
    // }

    baseTabs.push(
      {
        label: "Conditions",
        path: "conditions",
        icon: <RuleOutlined className="thin" />,
      },
      {
        label: "Exceptions",
        path: "exceptions",
        icon: <ReportProblemOutlined className="thin" />,
      },
      {
        label: "Sizer",
        path: "sizer",
        icon: <CalculateOutlined className="thin" />,
      },
    );

    if (!isEmpty(loanDrilldown?.appraisals)) {
      baseTabs.push({
        label: "Appraisals",
        path: "appraisals",
        icon: <GavelOutlined className="thin" />,
      });
    }

    // Closing readiness + closing documents + wire (closing-readiness.md).
    // Ungated: closers and Capital Markets need it and are not necessarily
    // underwriters. An acquisition schedules a trade SETTLEMENT, so the
    // label flips.
    baseTabs.push({
      label: isAcquisitions ? "Settlement" : "Closing",
      path: "closing",
      icon: <EventAvailableOutlined className="thin" />,
    });

    if ((isUnderwriter || isResfin || isLendmarq) && !isResilender) {
      baseTabs.push(
        // TEMP: LOI tab hidden
        // {
        //   label: "LOI",
        //   path: "letter-of-intent",
        //   icon: <DescriptionOutlined className="thin" />,
        // },
        {
          label: "Wire & Funding",
          path: "wire-and-funding",
          icon: <HandshakeOutlined className="thin" />,
        },
        {
          label: "Checklist",
          path: "checklist",
          icon: <ChecklistOutlined className="thin" />,
        },
      );
    }

    // AI Review — on-demand triggers for the loan AI agents. Admin-only, matching
    // the backend's require_admin gate on the run endpoints (run-agent-reviews.md).
    if (isAdmin) {
      baseTabs.push({
        label: "AI Review",
        path: "ai-review",
        icon: <AutoAwesomeOutlined className="thin" />,
      });
      // Admin — the permissions reference for this drilldown (every gated
      // area, who can use it, the rule). isAdmin already includes super admins.
      baseTabs.push({
        label: "Admin",
        path: "admin",
        icon: <AdminPanelSettingsOutlined className="thin" />,
      });
    }

    // Drop tabs the active pipeline doesn't support. Acquisitions (postclose)
    // has no application fees, deal terms or lock desk — those are preclose-only
    // (see src/pipelines/pipelineConfig.js). Routes stay mounted in
    // ContentRouter, so this only hides the nav entry.
    return baseTabs.filter((t) => {
      const requiredCapability = TAB_CAPABILITY_REQUIRED[t.path];
      return !requiredCapability || !!capabilities?.[requiredCapability];
    });
  }, [
    isUnderwriter,
    isAdmin,
    canViewLoanLock,
    isResfin,
    isLendmarq,
    isResilender,
    showChat,
    loanDrilldown?.appraisals,
    loanDrilldown?.loanProductType,
    isAcquisitions,
    appraisalOrderCount,
    capabilities,
  ]);

  return (
    <Box
      ref={navRef}
      sx={{
        flexShrink: 0,
        background: "#ffffff",
        borderTop: `1px solid #e2e8f0`,
        borderBottom: `1px solid #e2e8f0`,
        // Pinned to the top of #overflow-root (the drilldown's scroller) so the
        // tabs stay reachable while scrolling a long tab. `top: 0` is right —
        // the app TopHeader is an in-flow sibling of the scroller, not a fixed
        // overlay, so there's no header height to offset by.
        position: "sticky",
        top: 0,
        // Above the tab-content stickies (Documents toolbar/header sit at 3–5).
        // The sidebars are siblings of the scroller, so they still win.
        zIndex: 20,
      }}
    >
      <Flx ac sx={{ minWidth: 0 }}>
        <Flx sx={{ alignItems: "center", overflowX: "auto", flex: 1, minWidth: 0 }}>
          <LoanDrilldownRouterTabSwitcher
            tabs={tabs}
            iconSize={13}
            showIcons={showNavIcons}
            baseUrl={baseUrl}
            variant="background"
            subTabContext={{
              isAcquisitions,
              // The Contacts flyout lists General contractors only when the
              // section actually renders (construction products).
              isConstruction: isConstructionProduct(
                loanDrilldown?.loanProductType,
              ),
            }}
          />
        </Flx>
        {/* View selector moved to the header title row (between permitted users
            and the stage-progress bar). */}
      </Flx>
    </Box>
  );
});

/** =========================
 * DRILLDOWN CONTENT
 * ========================= */
const ContentRouter = memo(({ baseUrl }) => {
  const { user } = useAuth();
  const { isAdmin } = usePermissionsHook();
  return (
    <Box
      sx={{
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Routes>
        {/* Loan Chat — gated by the same allowlist/dev check as the nav tab. */}
        {canSeeLoanChat(user?.email) && (
          <Route path="/chat" element={<LoanDrilldownChatTab />} />
        )}
        {/* Default landing tab + canonical /summary. Both mount
            SummaryTabViewRouter so the header's view selector can swap to
            role-specific layouts (e.g. Closer View). */}
        <Route path="/" element={<SummaryTabViewRouter />} />
        <Route path="/summary" element={<SummaryTabViewRouter />} />
        {/* Loan Information — new left-nav + cards redesign. The classic tab
            stays mounted at /loan-information-classic so this is a one-line
            revert (swap the element below back to LoanDrilldownDetailsTab). */}
        <Route
          path="/loan-information"
          element={<LoanDrilldownLoanInfoRedesignTab />}
        />
        <Route
          path="/loan-information-classic"
          element={<LoanDrilldownDetailsTab />}
        />
        <Route path="/terms" element={<LoanDrilldownLoanTermsTab />} />
        <Route path="/fees" element={<LoanDrilldownFeesRedesignTab />} />
        <Route path="/budget-draws" element={<BudgetDrawsTab />} />
        <Route path="/loan-lock" element={<LoanDrilldownLoanLockTab />} />
        <Route
          path="/loan-lock-legacy"
          element={<LoanDrilldownLoanLockTabLegacy />}
        />
        <Route
          path="/dscr-calculations"
          element={<LoanDrilldownDscrCalculationsTab />}
        />
        <Route
          path="/dscr-workbook"
          element={<LoanDrilldownDscrWorkbookRedesignTab />}
        />
        <Route
          path="/liquidity-calculations"
          element={<LoanDrilldownLiquidityCalculationsTab />}
        />
        <Route
          path="/key-dates-and-documents"
          element={<LoanDrilldownKeyDatesAndDocumentsTab />}
        />
        <Route path="/files/*" element={<LoanDrilldownFilesTab />} />
        <Route path="/sizer" element={<LoanDrilldownSizerTab />} />
        <Route
          path="/contacts"
          element={<LoanDrilldownLoanRelatedContactsTab />}
        />
        {/* <Route
          path="/communication/*"
          element={<LoanDrilldownCommunicationTab />}
        /> */}
        {/* <Route
          path="/communication/:communicationType?"
          element={<LoanDrilldownCommunicationTab />}
        /> */}

        {/* /conditions now renders the toggle wrapper (Checklist ↔ Classic). */}
        <Route
          path="/conditions"
          element={<LoanDrilldownConditionsChecklistTab />}
        />
        <Route
          path="/conditions-checklist"
          element={<LoanDrilldownConditionsChecklistTab />}
        />
        <Route
          path="/conditions-redesign"
          element={<LoanDrilldownConditionsTab />}
        />
        <Route
          path="/conditions-and-exceptions"
          element={<LoanDrilldownConditionsAndExceptionsTab />}
        />
        {/* `/*` — every Documents sub-view is a sub-route
            (documents/uploaded, /parsed, /missing, …); the tab reads it. */}
        <Route path="/documents/*" element={<LoanDrilldownDocumentsTab />} />
        {/* Acquisitions-only; nav entry gated by the closedPackage capability,
            but the route stays mounted so deep links resolve. */}
        <Route
          path="/closed-package"
          element={<AcquisitionsClosedPackageTab />}
        />
        <Route path="/ai-review" element={<AiReviewTab />} />
        {/* Admin — same gate as the nav entry (admins + super admins). */}
        {isAdmin && <Route path="/admin" element={<LoanDrilldownAdminTab />} />}
        <Route path="/economics" element={<AcquisitionsEconomicsTab />} />
        <Route path="/orders" element={<LoanDrilldownOrdersTab />} />
        <Route
          path="/communication"
          element={<LoanDrilldownCommunicationTab />}
        />
        <Route
          path="/communication/:communicationType"
          element={<LoanDrilldownCommunicationTab />}
        />
        <Route
          path="/contacts/:contactId?"
          element={<LoanDrilldownLoanRelatedContactsTab />}
        />

        <Route path="/applicants" element={<LoanDrilldownApplicantsTab />} />
        {/* Experience Workbook — the "Experience Calc." tab. It used to live
            under Applicants; the old path redirects so bookmarks survive.
            Static segment, so the router ranks it above :borrowerId below. */}
        <Route path="/experience" element={<LoanDrilldownExperienceTab />} />
        <Route
          path="/applicants/experience"
          element={<Navigate to={`${baseUrl}/experience`} replace />}
        />
        {/* Borrower detail now opens via the LoanBorrowerSidebar from the
            Applicants tab. The legacy /borrowers tab was removed; these routes
            resolve deep links (incl. old /borrowers links repointed here) to
            the Applicants list. */}
        <Route
          path="/applicants/:borrowerId?"
          element={<LoanDrilldownApplicantsTab />}
        />
        <Route path="/borrowers" element={<LoanDrilldownApplicantsTab />} />
        <Route
          path="/borrowers/:borrowerId?"
          element={<LoanDrilldownApplicantsTab />}
        />

        <Route
          path="/properties"
          element={<LoanDrilldownSubjectPropertiesTab />}
        />
        <Route
          path="/properties/:propertyId?"
          element={<LoanDrilldownSubjectPropertiesTab />}
        />
        {/* Legacy properties tab kept mounted (not in nav) for deep links. */}
        <Route
          path="/properties-original/:propertyId?"
          element={<LoanDrilldownPropertiesTab />}
        />
        <Route
          path="/loan-related-contacts"
          element={<LoanDrilldownLoanRelatedContactsTab />}
        />
        <Route
          path="/loan-related-contacts/:contactId?"
          element={<LoanDrilldownLoanRelatedContactsTab />}
        />
        <Route path="/appraisals" element={<LoanDrilldownAppraisalsTab />} />
        <Route path="/entity" element={<LoanDrilldownEntityTab />} />
        {/* Legacy entity tab kept mounted (not in nav) for deep links. */}
        <Route
          path="/entity-original"
          element={<LoanDrilldownEntityOriginalTab />}
        />
        <Route path="/fees-and-closing/*" element={<LoanDrilldownFeesTab />} />
        <Route path="/closing/*" element={<LoanDrilldownClosingTab />} />
        {/* Wildcard: the tab's rail is route-driven, so each pane is its own
            sub-path (…/wire-and-funding/tie-out) and stays linkable. */}
        <Route
          path="/wire-and-funding/*"
          element={<LoanDrilldownWireAndFundingTab />}
        />
        <Route
          path="/letter-of-intent"
          element={<LoanDrilldownLetterOfIntentTab />}
        />
        <Route
          path="/terms-sheet"
          element={<LoanDrilldownTermsSheetHhbTab />}
        />
        <Route path="/exceptions" element={<LoanDrilldownExceptionsTab />} />
        <Route
          path="/exceptions-redesign"
          element={<LoanDrilldownExceptionsRedesignTab />}
        />
        <Route
          path="/loan-exceptions"
          element={<LoanDrilldownExceptionsTab />}
        />
        <Route path="/checklist" element={<LoanDrilldownChecklistTab />} />
        {/* Hidden card gallery — deliberately absent from NavigationBar.
            Reach it by typing /loan/<id>/test. Catalogues every card surface
            with its source path and call sites. */}
        <Route path="/test" element={<LoanDrilldownCardGalleryTab />} />
        <Route path="*" element={<Navigate to={`${baseUrl}`} replace />} />
      </Routes>
    </Box>
  );
});

const EditLoanButtonWithModals = memo(() => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { clearLoanDrilldownData } = useReduxLoanDrilldownHook();
  const [modal, setModal] = useState(null);

  const dispatch = useDispatch();
  const { getLoanById } = useUnderwritingHook();

  const onSuccessUpdate = useCallback(async (data) => {
    dispatch(loanDrilldownSet(data));
    dispatch(loanPipelineClear());
  }, []);

  const handleRefresh = () => {
    clearLoanDrilldownData();
    // dispatch(loanDrilldownClear());
    getLoanById({
      loanId: loanDrilldown?._id,
      onSuccessFn: (data) => {
        dispatch(loanDrilldownSet(data));
        // dispatch(sidebarReset());
      },
      onFailFn: () => dispatch(loanDrilldownSet({})),
    });
  };

  // const options = useMemo(
  //   () => [
  //     {
  //       label: "Edit Loan Name",
  //       onClick: () => setModal("name"),
  //       icon: <DriveFileRenameOutline />,
  //     },
  //     {
  //       label: "Edit Loan Tags",
  //       onClick: () => setModal("tags"),
  //       icon: <LocalOfferOutlined />,
  //     },
  //   ],
  //   []
  // );

  return (
    <Flx ac wrap>
      {/* <Tooltip title="Rename Loan Name">
        <Box>
          <DropdownButton
            size="small"
            icon={<EditOutlined fontSize="small" />}
            options={options}
          />
        </Box>
      </Tooltip> */}
      <Tooltip title="Edit Loan Name">
        <IconButton size="small" onClick={() => setModal("name")}>
          <EditOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Edit Loan Tags">
        <IconButton size="small" onClick={() => setModal("tags")}>
          <LocalOfferOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      <FavoriteLoanButton />
      <PinLoanTabButton />
      {/* <PinLoanButton /> */}
      <Tooltip title="Refresh loan data">
        <IconButton size="small" onClick={handleRefresh}>
          <RefreshOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      {/* Edit Correspondent, External Sync, and the pipeline Move / Archive
          actions now live in the header's <LoanSettingsMenu /> gear next to the
          View dropdown. */}

      <UpdateLoanDataModal
        loan={modal === "name" ? loanDrilldown : null}
        field={"loanName"}
        inputLabel={"New Loan Name"}
        type="text"
        onClose={() => setModal(null)}
        onSuccessFn={() => handleRefresh()}
      />
      <LoanTagsModal
        show={modal === "tags"}
        onClose={() => setModal(null)}
        onSuccessFn={onSuccessUpdate}
      />
    </Flx>
  );
});

const FAVORITES_STORAGE_KEY = nsKey("lo-pipeline-favorite-loans");

const getFavoriteLoanIds = () => {
  try {
    return JSON.parse(localStorage.getItem(FAVORITES_STORAGE_KEY)) || [];
  } catch {
    return [];
  }
};

const persistFavoriteLoanIds = (ids) =>
  localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(ids));

const FavoriteLoanButton = () => {
  const { syncPreferences } = useUserPreferences();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const [favoriteIds, setFavoriteIds] = useState(() => getFavoriteLoanIds());

  const loanId = loanDrilldown?._id;
  const isFavorite = loanId ? favoriteIds.includes(loanId) : false;

  const handleToggleFavorite = useCallback(() => {
    if (!loanId) return;
    setFavoriteIds((prev) => {
      const updated = prev.includes(loanId)
        ? prev.filter((id) => id !== loanId)
        : [...prev, loanId];
      persistFavoriteLoanIds(updated);
      return updated;
    });
    syncPreferences();
  }, [loanId, syncPreferences]);

  return (
    <Tooltip title={isFavorite ? "Remove from favorites" : "Add to favorites"}>
      <IconButton size="small" onClick={handleToggleFavorite}>
        {isFavorite ? (
          <StarRounded sx={{ fontSize: 20, color: "#f59e0b" }} />
        ) : (
          <StarOutlineRounded sx={{ fontSize: 20 }} />
        )}
      </IconButton>
    </Tooltip>
  );
};

const PinLoanTabButton = () => {
  const { toggleTab, isTabPinned, loading } = useWorkspaceTabs();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  // Record which pipeline the loan lives in so the pinned tab links to the
  // right drilldown base (/loan vs /acquisition).
  const { key: pipelineKey } = usePipeline();

  const loanId = loanDrilldown?._id;
  const loanNumber = loanDrilldown?.loanNumber;
  const loanName = loanDrilldown?.loanName;
  const pinned = loanNumber ? isTabPinned(loanNumber) : false;

  const handleToggle = useCallback(() => {
    if (!loanId || !loanNumber) return;
    toggleTab({ _id: loanId, loanNumber, loanName, pipeline: pipelineKey });
  }, [loanId, loanNumber, loanName, pipelineKey, toggleTab]);

  return (
    <Tooltip title={pinned ? "Unpin from tabs" : "Pin to tabs"}>
      <IconButton size="small" onClick={handleToggle} disabled={loading}>
        {loading ? (
          <CircularProgress size={18} sx={{ color: "#9ca3af" }} />
        ) : pinned ? (
          <PushPin sx={{ fontSize: 20, color: colors.action }} />
        ) : (
          <PushPinOutlined sx={{ fontSize: 20 }} />
        )}
      </IconButton>
    </Tooltip>
  );
};

/** =========================
 * HELPER COMPONENTS
 * ========================= */

// Single-field edits for the header's expected dates. Persist through the same
// LoanDrilldownChangesForm path the Loan Terms tab uses for root fields (the
// drilldown's PipelineProvider routes an acquisition to
// /loan_acquisitions/update_loan_data), so the write + redux refresh are handled.
const HEADER_DATE_FIELDS = {
  expectedClosingDate: [
    { name: "expectedClosingDate", label: "Expected Closing Date", type: "date" },
  ],
  // Acquisitions only — when we expect to settle (fund) the purchase.
  expectedSettlementDate: [
    { name: "expectedSettlementDate", label: "Expected Settlement Date", type: "date" },
  ],
};

// One header date: label, value (or "Not set") and a pencil (omitted when no
// `onEdit` — display only). `note` adds a small amber info icon whose tooltip
// carries it.
const HeaderDateField = ({ label, value, tooltip, onEdit, note }) => (
  <RffInputWrapper label={label} suppressGrid fullWidth={false}>
    <Flx ac g={0.25}>
      <Txt sx={{ fontSize: "11px", fontWeight: 600 }}>
        {value ? formatDate(value) : "Not set"}
      </Txt>
      {note && (
        <Tooltip title={note} arrow>
          <InfoOutlined sx={{ fontSize: 13, color: amber.main, cursor: "help" }} />
        </Tooltip>
      )}
      {onEdit && (
        <Tooltip title={tooltip} arrow>
          <IconButton size="small" onClick={onEdit} sx={{ p: 0.25 }}>
            <EditOutlined sx={{ fontSize: 13 }} className="thin" />
          </IconButton>
        </Tooltip>
      )}
    </Flx>
  </RffInputWrapper>
);

const OverviewDisplayFields = memo(() => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { isLaptopSized } = useMediaQueryHook();
  const correspondentOptions = useCorrespondentOptions();
  const navigate = useNavigate();
  const location = useLocation();
  const [correspondentSidebarOpen, setCorrespondentSidebarOpen] =
    useState(false);
  // Which header date is being edited (a HEADER_DATE_FIELDS key), or null.
  const [editDateField, setEditDateField] = useState(null);
  const isAcquisition = useIsAcquisitions();

  // Navigate to a drilldown tab, deriving the base so it works when the screen
  // is embedded under another route (e.g. the lock queue master-detail).
  const goToTab = useCallback(
    (tab) => {
      const loanId = loanDrilldown?._id;
      if (!loanId) return;
      const marker = `/${loanId}`;
      const idx = location.pathname.indexOf(marker);
      const base =
        idx === -1
          ? `/loan/${loanId}`
          : location.pathname.slice(0, idx + marker.length);
      navigate(`${base}/${tab}`);
    },
    [loanDrilldown?._id, location.pathname, navigate],
  );
  const filteredBrokerContacts = useMemo(() => {
    return loanDrilldown?.loanRelatedContacts?.filter(
      (c) => c?.loanRole === "Broker Contact",
    );
  }, [loanDrilldown?.loanRelatedContacts]);

  const brokerNames = useMemo(() => {
    const contacts = loanDrilldown?.loanRelatedContacts?.filter(
      (c) => c?.loanRole === "Broker Contact",
    );

    if (isEmpty(contacts)) {
      return null;
    }

    return contacts
      ?.map((c) => {
        return `${formatBorrowerObjectFullName(c)}`;
      })
      .join(", ");
  }, [loanDrilldown?.loanRelatedContacts]);

  // Entity name for the header: the deal no longer carries `borrowerEntity`.
  // Prefer the computed loan-level `entityName`, then the borrowing entity in
  // `entities[]` (falling back to the first entity), then the legacy field.
  const entityName = useMemo(() => {
    if (loanDrilldown?.entityName) return loanDrilldown.entityName;
    const entities = loanDrilldown?.entities;
    if (Array.isArray(entities) && entities.length > 0) {
      const borrowing = entities.find((e) => e?.borrowerEntity) || entities[0];
      if (borrowing?.entityName) return borrowing.entityName;
    }
    return loanDrilldown?.borrowerEntity?.entityName || null;
  }, [loanDrilldown]);

  // Resolve the loan's `correspondentCompanyId` to the correspondent's display
  // name via the cached correspondents list (same source the create-loan form
  // and the edit modal use).
  const correspondentName = useMemo(() => {
    const id = loanDrilldown?.correspondentCompanyId;
    if (isNil(id)) return null;
    const match = correspondentOptions.find(
      (o) => String(o.value) === String(id),
    );
    return match?.label || null;
  }, [loanDrilldown?.correspondentCompanyId, correspondentOptions]);

  return (
    <>
      <RffLoanDataGroup
        sx={{
          py: 1,
          px: 0.4,
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          // mt: 1,
          ".input-wrapper-root , .MuiDivider-root": {
            mr: 2,
          },
          ".input-wrapper-root": {
            flexShrink: 0,
          },
          // Overview header items render via RffDisplayField with suppressGrid,
          // so target the label/value elements directly (not nested under
          // .MuiGrid-root). Both label and value at 11px.
          ".MuiFormLabel-root , .MuiTypography-root": {
            whiteSpace: "nowrap",
            fontSize: "11px",
          },
        }}
        row
      >
        {/* Interactive loan-status control — the same dropdown (options +
            Confirm/Cancel) the Summary tab uses. */}
        <Box sx={{ flexShrink: 0, mr: 2 }}>
          <StatusChanger loan={loanDrilldown} compact />
        </Box>
        {/* Expected date — inline-editable straight from the header. An
            acquisition shows Expected Settlement only (when OUR money moves);
            the seller's Expected Closing lives on Loan Info and surfaces here
            only as a note while no settlement date is set. Pre-close shows
            Expected Closing. */}
        {isAcquisition ? (
          <>
            <HeaderDateField
              label="Expected Settlement"
              value={loanDrilldown?.expectedSettlementDate}
              tooltip="Edit expected settlement date"
              onEdit={() => setEditDateField("expectedSettlementDate")}
              note={
                !loanDrilldown?.expectedSettlementDate &&
                loanDrilldown?.expectedClosingDate
                  ? `This loan has an expected closing date of ${formatDate(loanDrilldown.expectedClosingDate)}`
                  : null
              }
            />
            {/* The correspondent's note date — display only; it is edited on
                Loan Info (underwriting / admin) once the loan has closed. */}
            <HeaderDateField
              label="Origination Date"
              value={loanDrilldown?.originationDate}
            />
          </>
        ) : (
          <HeaderDateField
            label="Expected Closing"
            value={loanDrilldown?.expectedClosingDate}
            tooltip="Edit expected closing date"
            onEdit={() => setEditDateField("expectedClosingDate")}
          />
        )}
        <RffDisplayField
          name="loanNumber"
          label="Loan #"
          type="select"
          data={loanDrilldown}
        />
        {loanDrilldown?.subStatus && (
          <RffDisplayField
            name={"subStatus"}
            label={"Sub-Status"}
            type={"select"}
            data={loanDrilldown}
          />
        )}
        {loanDrilldown?.sync_loan_id && (
          <RffDisplayField
            name={"sync_loan_id"}
            label={"Loan Sync ID"}
            // label={"External Loan Sync ID"}
            data={loanDrilldown}
          />
        )}

        {loanDrilldown?.last_integration_sync && (
          <RffDisplayField
            name={"last_integration_sync"}
            label={"Last Synced"}
            data={loanDrilldown}
            type={"date"}
          />
        )}

        <Seperator />
        <RffDisplayField
          name="loanProductType"
          // label="Product"
          label="Loan Product"
          type="select"
          data={loanDrilldown}
          maxWidth={isLaptopSized ? "120px" : null}
        />
        <RffDisplayField
          name="loanPurpose"
          // label="Purpose"
          label="Loan Purpose"
          type="select"
          data={loanDrilldown}
          maxWidth={isLaptopSized ? "120px" : null}
        />
        <RffDisplayField
          name="totalLoanAmount"
          label="Total Loan Amount"
          type="dollar"
          data={loanDrilldown}
        />
        <Seperator />
        {/* Entity Name — clickable chip (opens the Entity tab), matching the
            Guarantor / Property chip styling. */}
        <RffInputWrapper label="Entity Name" suppressGrid fullWidth={false}>
          {entityName ? (
            <HeaderValueChip
              label={entityName}
              tooltip={entityName}
              icon={
                <ApartmentOutlined
                  sx={{ fontSize: 13, color: HeaderChipT.ink3 }}
                />
              }
              onClick={() => goToTab("entity")}
              maxWidth={isLaptopSized ? 140 : "none"}
            />
          ) : (
            <Flx ac g={0.5} sx={{ color: red[500] }}>
              <ErrorOutline className="thin" sx={{ fontSize: 14 }} />
              <Txt sx={{ fontSize: 12, fontWeight: 600 }}>Not Set</Txt>
            </Flx>
          )}
        </RffInputWrapper>
        {loanDrilldown?.correspondentCompanyId && (
          <RffInputWrapper label="Correspondent" suppressGrid fullWidth={false}>
            <HeaderValueChip
              label={correspondentName || "—"}
              tooltip={correspondentName || undefined}
              icon={
                <BusinessOutlined
                  sx={{ fontSize: 13, color: HeaderChipT.ink3 }}
                />
              }
              onClick={() => setCorrespondentSidebarOpen(true)}
              maxWidth={isLaptopSized ? 140 : "none"}
            />
          </RffInputWrapper>
        )}
        {isNil(brokerNames) ? null : (
          <RffDisplayField
            label={
              size(filteredBrokerContacts) > 1
                ? `Brokers (${size(filteredBrokerContacts)})`
                : "Broker"
            }
            value={brokerNames}
            maxWidth={isLaptopSized ? "140px" : null}
          />
        )}

        {/* Guarantors + properties render as chips with a "+N more" overflow
            popover rather than comma-joined strings — the header stays one row
            tall no matter how many there are, and each chip opens that entity's
            detail sidebar. */}
        <LoanHeaderGuarantors />
        <LoanHeaderProperties />
        {/* <Seperator />
      <LoanPermittedUsersAvatarStack /> */}
      </RffLoanDataGroup>
      <LoanCorrespondentSidebar
        correspondentId={loanDrilldown?.correspondentCompanyId}
        open={correspondentSidebarOpen}
        onClose={() => setCorrespondentSidebarOpen(false)}
      />
      <UpdateLoanRootDataFieldsModal
        show={Boolean(editDateField)}
        onClose={() => setEditDateField(null)}
        loanId={loanDrilldown?._id}
        fields={HEADER_DATE_FIELDS[editDateField] || []}
        maxWidth="xs"
      />
    </>
  );
});

// const LoanPermittedUsersAvatarStack = ({ children }) => {
//   const loanDrilldown = useSelector((state) => state.loanDrilldown);
//   const { updateLoanPermittedUsers, loading } = useUnderwritingHook();
//   const dispatch = useDispatch();
//   const users = useSelector((state) => state.users);
//   const [show, setShow] = useState(false);

//   const permittedUsersNames = useMemo(() => {
//     const permittedUsers = loanDrilldown?.permittedUsers;
//     if (isEmpty(permittedUsers)) {
//       return "None";
//     }

//     const permittedUsersFullnames = permittedUsers.map((user) => {
//       return getUserFullNameFromEmail(users, user);
//     });

//     return permittedUsersFullnames.join(", ");
//   }, [loanDrilldown?.permittedUsers, users]);

//   const permittedUsersFullNames = useMemo(() => {
//     const permittedUsers = loanDrilldown?.permittedUsers;
//     if (isEmpty(permittedUsers)) {
//       return [];
//     }
//     return permittedUsers.map((user) => {
//       return getUserFullNameFromEmail(users, user);
//     });
//   }, [loanDrilldown?.permittedUsers, users]);

//   const initialValues = useMemo(() => {
//     return {
//       permittedUsers: loanDrilldown?.permittedUsers || [],
//     };
//   }, [loanDrilldown?.permittedUsers]);

//   const onSubmit = (values) => {
//     const updatedPermittedUsers = values?.permittedUsers;
//     console.log("userEmail", updatedPermittedUsers);
//     updateLoanPermittedUsers({
//       loanId: loanDrilldown?._id,
//       permittedUsers: updatedPermittedUsers,
//       onSuccessFn: (res) => {
//         dispatch(loanDrilldownSet(res));
//         setShow(false);
//       },
//     });
//   };

//   return (
//     <>
//       {/* <Box sx={{ position: "relative" }}>
//         <Tooltip title="Add permitted users to view this loan">
//           <IconButton
//             sx={{
//               position: "absolute",
//               left: "115px",
//               top: "-4px",
//               zIndex: 1,
//               color: "text.secondary",
//             }}
//             size="small"
//             onClick={() => setShow(true)}
//           >
//             <AddRounded fontSize="small" />
//           </IconButton>
//         </Tooltip>

//         <RffDisplayField
//           label={`Permitted Users (${size(loanDrilldown?.permittedUsers)})`}
//           value={permittedUsersNames}
//           maxWidth="300px"
//         />
//       </Box> */}
//       <AvatarStackWithAction
//         label="Permitted Users"
//         onAddClick={() => setShow(true)}
//         onAddTooltip="Add permitted users to view this loan"
//         items={permittedUsersFullNames}
//       />

//       <BasicModal
//         show={show}
//         onClose={() => setShow(false)}
//         title="Permitted Users"
//         maxWidth="sm"
//       >
//         <Flx column gap={1} sx={{ mt: 1, mb: 3 }}>
//           <RffDisplayField
//             label={`Current Permitted Users (${size(loanDrilldown?.permittedUsers)})`}
//             value={permittedUsersNames}
//           />
//         </Flx>
//         <Divider sx={{ mb: 3 }} />
//         <RffForm
//           loading={loading}
//           onSubmit={onSubmit}
//           submitText="Add User"
//           initialValues={initialValues}
//         >
//           <RffGroup>
//             <RffSelectMultipleUserEmailField
//               name="permittedUsers"
//               displayFullLabel
//               label="Select user to add to loan"
//             />
//           </RffGroup>
//         </RffForm>
//       </BasicModal>
//     </>
//   );
// };

const Seperator = ({ children }) => {
  return <Divider orientation="vertical" flexItem sx={{ mx: 0 }} />;
};

export default LoanDrilldownScreen;
